import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { SplashScreen } from '@capacitor/splash-screen';
import { Router } from '@angular/router';
import { Inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { APP_CONFIG, IAppConfig } from '@app/core/config';
import { DialogAlertUpdateComponent } from '@app/views/pages/mobile-alerts/components/dialog-alert-update/dialog-alert-update.component';

export interface VersionCheckResponse {
  needUpdate: boolean;
  version: string;
  forced: boolean;
  marketUrl: string;
  releaseDate: string;
  serviceDisabled: boolean;
}

@Injectable({ providedIn: 'root' })
export class AppVersionCheckService {

  private readonly dismissedVersionKey = 'dismissedUpdateVersion';

  constructor(
    private http: HttpClient,
    private router: Router,
    private matDialog: MatDialog,
    @Inject(APP_CONFIG) private appConfig: IAppConfig
  ) { }

  async checkVersion(): Promise<void> {
    // فقط روی اپ نیتیو (اندروید/iOS) اجرا شود
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const safetyTimeout = setTimeout(() => this.hideSplash(), 10000);

    try {
      const info = await App.getInfo();
      const currentVersion = info.version;
      const platformType = Capacitor.getPlatform() === 'ios' ? 2 : 1;

      const response: any = await this.http.post(
        `${this.appConfig.apiEndpoint}/v2/Application`,
        {
          platformType,
          version: currentVersion
        }
      ).toPromise();

      const data: VersionCheckResponse = response?.data;

      if (!data) {
        await this.hideSplash();
        return;
      }

      if (data.serviceDisabled) {
        await this.hideSplash();
        this.router.navigate(['/security-app/service-disabled']);
        return;
      }

      if (data.needUpdate && data.forced) {
        await this.hideSplash();
        this.router.navigate(['/security-app/force-update'], {
          state: {
            marketUrl: data.marketUrl,
            forced: data.forced,
            latestVersion: data.version
          }
        });
        return;
      }

      if (data.needUpdate && !data.forced) {
        await this.hideSplash();
        const dismissedVersion = localStorage.getItem(this.dismissedVersionKey);
        if (dismissedVersion === data.version) {
          return;
        }
        this.openUpdateDialog(data);
        return;
      }

      // همه چیز اوکیه — splash رو ببند و بگذار اپ عادی ادامه پیدا کند
      await this.hideSplash();

    } catch (error) {
      // اگه API در دسترس نبود، اپ رو بلاک نکن — fail-open
      console.error('Version check failed', error);
      await this.hideSplash();
    } finally {
      clearTimeout(safetyTimeout);
    }
  }

  private async hideSplash(): Promise<void> {
    try {
      await SplashScreen.hide();
    } catch (e) {
      // اگه پلاگین یا پلتفرم splash نداشت، نادیده بگیر
    }
  }

  private openUpdateDialog(data: VersionCheckResponse): void {
    const dialogRef = this.matDialog.open(DialogAlertUpdateComponent, {
      width: "380px",
      maxWidth: "96.612%",
      minHeight: "176px",
      autoFocus: false,
      panelClass: "new-custom-modalbox",
      restoreFocus: false,
      data: {
        marketUrl: data.marketUrl,
        latestVersion: data.version,
        forced: false
      }
    });

    dialogRef.afterClosed().subscribe(() => {
      localStorage.setItem(this.dismissedVersionKey, data.version);
    });
  }
}